import React from 'react';
import { useInView } from 'react-intersection-observer';
import './Foundation.css';

const Foundation = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const foundations = [
    {
      icon: 'fas fa-calculator',
      title: 'The Foundation of Finance & Control',
      credential: 'CPA, Financial Acumen',
      description: 'My career foundation was built on rigorous accounting principles and formalized through CPA licensure, ensuring every strategic decision is anchored in robust Financial and Accounting Acumen, with an unwavering commitment to corporate profitability, financial transparency, and transaction integrity that drives sustainable business value.',
      color: '#C89B3C'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'The Core of Assurance and Advisory',
      credential: 'CIA, Internal Audit',
      description: 'As a seasoned Assurance and Advisory leader with deep expertise in evaluating control effectiveness and optimizing organizational processes across diverse functions and global geographies, I deliver strategic insights that transform risk into opportunity and drive operational resilience, with this mastery formalized through my CIA credential.',
      color: '#C89B3C'
    },
    {
      icon: 'fas fa-balance-scale',
      title: 'The Pillars of Governance, Risk, and Compliance',
      credential: 'GRCP, GRCA, ERM',
      description: 'With extensive experience leading enterprise-wide Governance, Risk, and Compliance (GRC) functions, I have developed and implemented robust governance frameworks that seamlessly integrate Compliance programs and Enterprise Risk Management (ERM) strategies across complex, multi-jurisdictional organizations, with expertise formalized through my GRCP and GRCA certifications.',
      color: '#C89B3C'
    },
    {
      icon: 'fas fa-lock',
      title: 'The Shield of Security & Integrity',
      credential: 'CFE, CISA, CC',
      description: 'I safeguard the integrity and ethicality of the business environment through a multi-layered approach. My CFE credential enables sophisticated Fraud Risk Management and complex investigations, while my CISA and Certified in Cybersecurity (CC) certifications provide comprehensive oversight of Information Systems and Cybersecurity risk, ensuring robust protection across all digital and operational frontiers.',
      color: '#C89B3C'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'The Engine of Improvement',
      credential: 'IPMP, Business Excellence',
      description: 'With proven success in establishing and scaling the Internal Control and Global Business Excellence function from the ground up, I have driven transformative Policy Development & Control Frameworks, enterprise-wide procedure standardization, and sustained Continuous Improvement initiatives across complex, multi-geographic organizations—expertise validated and elevated by the prestigious IPMP certification.',
      color: '#C89B3C'
    }
  ];

  const strategicAdvisor = [
    {
      icon: 'fas fa-sitemap',
      title: 'Integrated Solutions',
      description: 'I direct the integrated Governance, Audit, Risk, and Compliance (GARC) function, seamlessly connecting these typically siloed areas into a unified, enterprise-wide framework that drives strategic alignment and organizational resilience.'
    },
    {
      icon: 'fas fa-users-cog',
      title: 'Leadership and Scale',
      description: 'I have established and scaled global functions, including Internal Audit, Internal Control, Business Excellence, and GRC from the ground up in multiple organizations, providing strategic counsel directly to Senior Management, Audit Committees, and Boards of Directors for a global enterprise spanning over 125 legal entities with a combined value exceeding $2 billion across diverse geographies and industries.'
    },
    {
      icon: 'fas fa-hands-helping',
      title: 'Deep, Direct Experience',
      description: 'My expertise spans the entire business value chain, built on direct, hands-on experience in every critical role. I\'ve been on both sides—as the auditee and the auditor, the process owner and the designer of policies and Control Self-Assessment (CSA) programs. This end-to-end perspective, combined with leading complex global engagements and implementing innovative solutions like penetration testing, coaching, training, and awareness sessions that proves my mastery of all components driving organizational success.'
    }
  ];

  return (
    <section id="foundation" className="foundation-section" ref={ref}>
      <div className="container">
        {/* Header */}
        <div className={`foundation-header ${inView ? 'fade-in' : ''}`}>
          <h2 className="section-title center">THE INTERDISCIPLINARY POWERHOUSE</h2>
          <div className="title-underline center"></div>
          <p className="foundation-intro">
            Melgar Gultiano is an executive powerhouse, holding credentials like CPA, CIA, CFE, CISA, CC, GRCP, GRCA, 
            and IPMP. This unique combination of certifications is not merely a collection of titles; it represents an 
            end-to-end interdisciplinary exposure that allows for strategic guidance, not just mere oversight.
          </p>
        </div>

        {/* 360-Degree Foundation */}
        <div className="foundation-360-section">
          <h3 className="subsection-title">A 360-Degree Interdisciplinary Foundation</h3>
          <p className="subsection-intro">
            His two decades of experience have deliberately spanned and synthesized critical domains of business 
            assurance and strategy, establishing a truly polymathic foundation:
          </p>

          <div className="foundations-grid">
            {foundations.map((foundation, index) => (
              <div 
                key={index} 
                className={`foundation-card ${inView ? 'fade-in' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="foundation-icon-wrapper">
                  <div className="foundation-icon">
                    <i className={foundation.icon}></i>
                  </div>
                  <div className="foundation-number">{index + 1}</div>
                </div>
                <div className="foundation-content">
                  <h4 className="foundation-title">{foundation.title}</h4>
                  <div className="foundation-credential">
                    <i className="fas fa-certificate"></i>
                    {foundation.credential}
                  </div>
                  <p className="foundation-description">{foundation.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Strategic Advisor Section */}
        <div className={`strategic-advisor-section ${inView ? 'fade-in' : ''}`}>
          <h3 className="subsection-title">A Uniquely Positioned Strategic Advisor</h3>
          <p className="subsection-intro">
            This multi-faceted expertise positions him for a firm delivery of strategic guidance, not mere oversight:
          </p>

          <div className="advisor-grid">
            {strategicAdvisor.map((item, index) => (
              <div 
                key={index} 
                className={`advisor-card ${inView ? 'fade-in' : ''}`}
                style={{ animationDelay: `${(index + 5) * 0.1}s` }}
              >
                <div className="advisor-icon">
                  <i className={item.icon}></i>
                </div>
                <h4 className="advisor-title">{item.title}</h4>
                <p className="advisor-description">{item.description}</p>
                <div className="advisor-accent-line"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Banner */}
        <div className={`foundation-summary ${inView ? 'fade-in' : ''}`}>
          <div className="summary-content">
            <i className="fas fa-trophy"></i>
            <div className="summary-text">
              <h4>The Result: Strategic Excellence</h4>
              <p>
                Combining deep technical expertise across finance, audit, risk, compliance, security, and business 
                excellence with proven leadership in establishing and scaling global functions for a $2B+ enterprise.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Foundation;

